import React, { Component } from 'react';
import { Row, Col } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import { safeDivide } from 'helpers/utilities';
import { reduceLayerTwoData } from 'helpers/data';
import { toolTipPointFormatterGenerator, labelFormatterGenerator } from 'helpers/charts';
import RenderingTile from 'components/molecules/RenderingTile';
import TwoAxisBarWithScatterChart from 'components/molecules/charts/TwoAxisBarWithScatterChart';
import TwoAxisBarWithLineChart from 'components/molecules/charts/TwoAxisBarWithLineChart';
import DonutChart from 'components/molecules/charts/DonutChart';
import SocialAdSetOverviewPivotTable from 'components/molecules/tables/SocialAdSetOverviewPivotTable';
import SocialConversionsChart from 'components/molecules/charts/SocialConversionsChart';
import { LAYERTHREE, LAYERTWO } from 'constants/layers';
import { getActivitiesSettings } from 'helpers/objectiveSettings';

// LayerTwo Charts & Tables
import LayerTwoSummaryTable from 'components/molecules/tables/LayerTwoSummaryTable';
import SocialActionOverviewTable from 'components/molecules/tables/SocialActionOverviewTable';

class GenericSocialExpandedModule extends Component {
    render() {
        const {
            objective,
            xAxisLabel,
            backfillBuckets,
            layer = LAYERTHREE,
            layerTwo,
            showConversionRevenue,
            entityMeta,
            entityObjective,
            entityObjectiveLabelCode
        } = this.props;
        const {
            conversionTermSingular,
        } = getActivitiesSettings(entityObjective, entityObjectiveLabelCode);
        const stub = `l${layer}-social`;
        const { adsets, campaigns, timeData, ads, actions } = this.props.module;
        const totalConversions = get(adsets, 'statistics.conversions', 0);
        const totalImpressions = get(adsets, 'statistics.impressions', 0);
        const hasAdSetData = adsets.hasData;
        const hasAdsData = ads.hasData;
        const l2Data = get(layerTwo, 'social', {});
        const hasLayerTwoSummaryData = get(l2Data, 'summary.hasData', null);
        const aliasDescriptor = get(this.props, 'entityMeta.childAliasNames.pick', 'LOCATION');
        const userId = get(this.props, 'currentUser.id', null);
        const entityId = get(this.props, 'entityId', null);

        let childSummaryTableData = [];
        if (hasLayerTwoSummaryData) {
            const reducedData = reduceLayerTwoData(l2Data.summary);
            childSummaryTableData = reducedData.childSummaryTableData;
        }

        const rankedConversions = hasAdSetData ? adsets.rankings.conversions.reduce((prev, data) => {
            const adSetName = adsets.adSetIdMap[data.name].name;
            const statistics = adsets[data.name].statistics;
            prev.data.push(statistics);
            prev.categories.push(adSetName);

            if (statistics.conversions > 0) {
                prev.percentages.push({
                    name: adSetName,
                    value: safeDivide(statistics.conversions, prev.total)
                });
            }

            return prev;
        }, { categories: [], data: [], total: totalConversions, percentages: [] }) : {};

        const rankedImpressionsByAdset = hasAdSetData ? adsets.rankings.impressions.reduce((prev, data) => {
            const adsetName = adsets.adSetIdMap[data.name].name;
            if (data.value > 0) {
                prev.data.push({
                    name: adsetName,
                    y: data.value
                });
            }
            return prev;
        }, { data: [], total: totalImpressions }) : {};

        const rankedImpressionsByAd = hasAdsData ? ads.rankings.impressions.reduce((prev, data) => {
            const adName = ads.adIdMap[data.name].name;
            if (data.value > 0) {
                prev.data.push({
                    name: adName,
                    y: data.value
                });
            }
            return prev;
        }, { data: [], total: totalImpressions }) : {};

        const { data } = timeData;
        const hasTimeData = get(timeData, 'hasData', null);
        const objectives = get(entityMeta, 'social.objectives', []);
        const showLayerTwoSummaryTable = !!(hasLayerTwoSummaryData && (layer === LAYERTWO) && (objectives.length > 1));
        const showCampaignSummaryTable = !!(hasAdSetData && layer === LAYERTHREE);

        const layerTwoSummaryAccessors = ['impressions', 'clicks', 'cpc', 'link_clicks', 'cost_per_link_click', 'conversions', 'conversion_rate', 'engagements', 'engagement_rate', 'spend'];

        return (

            <React.Fragment>
                {hasTimeData ? (
                    <TwoAxisBarWithLineChart
                        title="Impressions VS. CTR"
                        exportName={`${stub}-generic-${objective}-impressions-vs-ctr`}
                        data={data}
                        yBar="impressions"
                        yBarOptions={{ formatByType: 'number' }}
                        yLine="ctr"
                        yLineTitle="CTR"
                        yLineOptions={{ formatByType: 'percentage' }}
                        toolTipOptions={{
                            pointFormatter: toolTipPointFormatterGenerator
                        }}
                        yAxisLineOptions={{
                            labels: {
                                formatter: labelFormatterGenerator('percentage')
                            }
                        }}
                        xAxisLabel={xAxisLabel}
                        backfillBuckets={backfillBuckets}
                        animateDuration={2000}
                        xAxisType="datetime" />
                ) : (
                    <RenderingTile height='400px' show={hasTimeData} />
                )}

                <Row>
                    <Col md={6}>
                        {hasAdSetData ? (
                            <DonutChart
                                title='Impressions By Ad Set'
                                exportName={`${stub}-generic-${objective}-impressions-by-adset`}
                                data={rankedImpressionsByAdset.data}
                                y="impressions"
                                total={rankedImpressionsByAdset.total}
                                totalTitle="Total Impressions"
                                totalFormat='0,0' />
                        ) : (
                            <RenderingTile height='465px' show={hasAdSetData} />
                        )}
                    </Col>
                    <Col md={6}>
                        {hasAdsData ? (
                            <DonutChart
                                title='Impressions By Ad'
                                exportName={`${stub}-generic-${objective}-impressions-by-ad`}
                                data={rankedImpressionsByAd.data}
                                y="impressions"
                                total={rankedImpressionsByAd.total}
                                totalTitle="Total Impressions"
                                totalFormat='0,0' />
                        ) : (
                            <RenderingTile height='465px' show={hasAdsData} />
                        )}
                    </Col>
                </Row>

                {hasTimeData ? (
                    <TwoAxisBarWithLineChart
                        title="Impressions VS. Engagement Rate"
                        exportName={`${stub}-generic-${objective}-impressions-vs-engagement-rate`}
                        data={data}
                        yBar="impressions"
                        yBarOptions={{ formatByType: 'number' }}
                        yLine="engagement_rate"
                        yLineTitle="Engagement Rate"
                        yLineOptions={{ formatByType: 'percentage' }}
                        toolTipOptions={{
                            pointFormatter: toolTipPointFormatterGenerator
                        }}
                        yAxisLineOptions={{
                            labels: {
                                formatter: labelFormatterGenerator('percentage')
                            }
                        }}
                        xAxisLabel={xAxisLabel}
                        backfillBuckets={backfillBuckets}
                        animateDuration={2000}
                        xAxisType="datetime" />
                ) : (
                    <RenderingTile height='400px' show={hasTimeData} />
                )}

                {hasAdSetData ?
                    (
                        <TwoAxisBarWithScatterChart
                            title="CONVERSIONS VS. COST / CONVERSION"
                            exportName={`l3-social-generic-${objective}-conversions-vs-cost-per-converion`}
                            data={rankedConversions.data}
                            yBar="conversions"
                            scatter="cost_per_conversion"
                            scatterTitle="Cost / Conversion"
                            toolTipOptions={{
                                pointFormatter: toolTipPointFormatterGenerator
                            }}
                            yScatterOptions={{
                                labels: {
                                    formatter: labelFormatterGenerator('priceWithoutSpace')
                                }
                            }}
                            yScatterUserOptions={{ formatByType: 'priceWithoutSpace' }}
                            animateDuration={2000}
                            categories={rankedConversions.categories} />
                    ) : (
                        <RenderingTile height='400px' show={hasAdsData} />
                    )}

                <SocialConversionsChart actions={actions} exportNamePrefix={`${stub}-${objective}`} />

                {showLayerTwoSummaryTable ? (
                    <LayerTwoSummaryTable
                        title={`${aliasDescriptor} OVERVIEW`}
                        channel={'social'}
                        aliasDescriptor={aliasDescriptor}
                        data={childSummaryTableData}
                        showConversionRevenue={showConversionRevenue}
                        exportName={`l2-social-generic-${objective}-table-performance`}
                        accessors={layerTwoSummaryAccessors}
                        cacheKey={`l2-social-generic-${objective}-table-performance-${userId}-${entityId}`}
                    />
                ) : (
                    <RenderingTile height='400px' show={showLayerTwoSummaryTable} />
                )}

                {showCampaignSummaryTable ? (
                    <SocialAdSetOverviewPivotTable
                        title="Campaign Overview"
                        showConversionRevenue={showConversionRevenue}
                        exportName={`l3-social-generic-${objective}-summary-adsets-overview`}
                        adSets={adsets} />
                ) : (
                    <RenderingTile height='400px' show={showCampaignSummaryTable}/>
                )}

                <SocialActionOverviewTable
                    exportName={`l3-social-generic-${objective}-action-overview`}
                    actions={actions}
                    campaigns={campaigns}
                    conversionTermSingular={conversionTermSingular}
                    cacheKey={`l3-social-generic-${objective}-action-overview-${userId}-${entityId}`}
                />

            </React.Fragment>
        );
    }
}

GenericSocialExpandedModule.propTypes = {
    module: PropTypes.shape({
        actions: PropTypes.object,
        campaigns: PropTypes.object,
        adsets: PropTypes.object,
        ads: PropTypes.object,
        timeData: PropTypes.object
    }).isRequired
};

export default GenericSocialExpandedModule;



// WEBPACK FOOTER //
// ./src/components/organisms/social/GenericSocialExpandModule.js